import { getDrawStatusKey } from '../drawStatus';
import { GameEmblemDecoration } from '@src/theme/Decoration';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@src/components';
import { boxOrganizerPath } from '@src/routing/routes';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { Trans, useTranslation } from 'react-i18next';
import { BoxPicture } from '@src/components/BoxPicture/BoxPicture';
import { ThemeArtwork } from '@src/theme/Artwork';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { OrganizerIdentity } from './OrganizerIdentity';
import './HangingGameSign.css';

/** Shared hanging exchange identity, used by both mobile and desktop. */
export function HangingGameSign({
  info,
  onChanged,
}: {
  info: BoxInfo;
  cards: CardInfo[];
  onChanged: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const box = info.box;
  const archived = Boolean(box.isArchived);
  const status = t(getDrawStatusKey(info));
  return (
    <HangingGameSignArtwork name={box.name} picture={box.picture} logo={box.logo}>
      <HangingGameSignDetails
        organizer={(info.admin?.username || info.organizers?.length) ? <OrganizerIdentity info={info} onChanged={onChanged} /> : undefined}
        drawn={info.isActiveDraw && !archived}
        status={
          info.isAdmin && !info.isActiveDraw && !archived ? (
            <Trans
              i18nKey="game.main.drawPendingAction"
              components={{
                action: (
                  <Link
                    variant="inline"
                    href={`${boxOrganizerPath(box.key)}#draw`}
                    testId="game-draw-pending-action"
                  />
                ),
              }}
            />
          ) : (
            status
          )
        }
      />
    </HangingGameSignArtwork>
  );
}

export function HangingGameSignDetails({
  organizer,
  drawn,
  status,
}: {
  organizer: ReactNode;
  drawn?: boolean;
  status: ReactNode;
}) {
  return (
    <div className="box-main__sign-details">
      <p className="box-main__sign-organizer">{organizer}</p>
      <span className="box-main__mobile-status">
        {drawn ? <CheckIcon /> : <ClockIcon />}
        <span>{status}</span>
      </span>
    </div>
  );
}

export function HangingGameSignArtwork({
  name,
  picture,
  logo,
  children,
}: {
  name: string;
  picture?: string | null;
  logo?: string | null;
  children?: ReactNode;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [wrapped, setWrapped] = useState(false);
  useLayoutEffect(() => {
    const title = titleRef.current;
    if (!title) return;
    const measure = () => {
      // Measure at the regular size so shrinking a long title cannot toggle
      // the wrapped class back and forth when it fits on one line.
      const probe = title.cloneNode(true) as HTMLHeadingElement;
      probe.removeAttribute('id');
      probe.setAttribute('aria-hidden', 'true');
      Object.assign(probe.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        width: `${title.parentElement!.clientWidth}px`,
        fontSize: 'var(--game-title-font-size, clamp(17px, 5.6cqw, 23px))',
      });
      title.parentElement!.append(probe);
      const lineHeight = parseFloat(getComputedStyle(probe).lineHeight);
      const needsWrap = probe.offsetHeight > lineHeight * 1.5;
      probe.remove();
      setWrapped(needsWrap);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(title);
    measure();
    return () => observer.disconnect();
  }, [name]);

  return (
    <div className={`box-main__hanging-sign${wrapped ? ' box-main__hanging-sign--wrapped' : ''}`}>
      <ThemeArtwork role="exchange" className="box-main__sign-artwork" />
      <div className="box-main__sign-content">
        <div className="box-main__sign-emblem" aria-hidden="true">
          <GameEmblemDecoration />
          <div className="box-main__sign-picture">
            <BoxPicture picture={picture} logo={logo} />
          </div>
          <GameEmblemDecoration />
        </div>
        <h1 ref={titleRef}>{name}</h1>
        {children}
      </div>
    </div>
  );
}
